import React from "react";
import { BillingInfo } from "../../../types/billing";
import {
  ExclamationTriangleIcon,
  ArrowUpIcon,
} from "@heroicons/react/24/outline";

interface UsageIndicatorsProps {
  billingData: BillingInfo;
  onUpgradeClick?: () => void;
}

export const UsageIndicators: React.FC<UsageIndicatorsProps> = ({
  billingData,
  onUpgradeClick,
}) => {
  const getUsageWarnings = () => {
    const warnings: Array<{
      type: string;
      current: number;
      max: number;
      percentage: number;
      severity: "warning" | "critical";
      actionable: boolean;
    }> = [];

    // Only show editor usage warning if it's actionable (can upgrade)
    if (billingData.max_editors) {
      const editorPercentage =
        (billingData.current_editors / billingData.max_editors) * 100;
      if (editorPercentage >= 90) {
        // Higher threshold since this is actionable
        warnings.push({
          type: "Editor Seats",
          current: billingData.current_editors,
          max: billingData.max_editors,
          percentage: editorPercentage,
          severity: editorPercentage >= 100 ? "critical" : "warning",
          actionable: true, // User can add more seats or upgrade
        });
      }
    }

    return warnings;
  };

  const warnings = getUsageWarnings();

  // Don't render anything if no actionable warnings
  if (warnings.length === 0) {
    return null;
  }

  const handleAction = (warning: any) => {
    if (warning.type === "Editor Seats") {
      // Expand the subscription manager to add seats
      if (onUpgradeClick) {
        onUpgradeClick();
      }
    }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
      <div className="flex items-start">
        <ExclamationTriangleIcon className="h-5 w-5 text-amber-400 mt-0.5 flex-shrink-0" />
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-amber-900">Capacity Alert</h3>
          <div className="mt-2 space-y-3">
            {warnings.map((warning) => (
              <div key={warning.type}>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-amber-800">{warning.type}</span>
                  <span
                    className={`text-sm font-medium ${
                      warning.severity === "critical"
                        ? "text-red-600"
                        : "text-amber-600"
                    }`}
                  >
                    {warning.current} / {warning.max}
                  </span>
                </div>
                <div className="w-full bg-amber-200 rounded-full h-2 mb-3">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      warning.severity === "critical"
                        ? "bg-red-500"
                        : "bg-amber-500"
                    }`}
                    style={{ width: `${Math.min(warning.percentage, 100)}%` }}
                  />
                </div>
                {warning.actionable && (
                  <button
                    onClick={() => handleAction(warning)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-amber-900 bg-amber-100 hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500"
                  >
                    {warning.type === "Editor Seats"
                      ? "Add More Seats"
                      : "Upgrade Plan"}
                    <ArrowUpIcon className="ml-1 h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
